import React, { useEffect, useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createWordAndAddToStudySet, updateWordAndAddToStudySet } from 'services/Teacher/Vocabulary.service';

const AddNewWordModal = ({ show, handleClose, studySetId, reload, wordData }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [newWordDetails, setNewWordDetails] = useState({
        _id: wordData?._id,
        title: wordData?.title || "",
        translations: wordData?.translations[0]?.text || "",
        sentence: wordData?.sentence || "",
        difficulty: wordData?.difficulty || "Beginner",
        meaning: wordData?.meaning || "",
        classType: wordData?.class || "noun",
    });

    useEffect(() => {
        if (show) {
            if (wordData) {
                setNewWordDetails({
                    _id: wordData._id,
                    title: wordData.title || "",
                    translations: wordData.translations?.[0]?.text || "",
                    sentence: wordData.sentence || "",
                    difficulty: wordData.difficulty || "Beginner",
                    meaning: wordData.meaning || "",
                    classType: wordData.class || "noun",
                });
            } else {
                setNewWordDetails({
                    _id: "",
                    title: "",
                    translations: "",
                    sentence: "",
                    difficulty: "Beginner",
                    meaning: "",
                    classType: "noun",
                });
            }
        }
    }, [wordData, show]);

    const handleCreateNewWord = async () => {
        const { title, translations, sentence, difficulty, meaning, classType } = newWordDetails;

        // Validation check for required fields
        if (!title || !translations || !studySetId) {
            alert(t("Please fill in all required fields."));
            return;
        }

        const payload = {
            title,
            translations,
            sentence,
            difficulty,
            meaning,
            language: translations,  // assuming translation is for target language
            creationMethod: "manual",  // assuming manual creation method
            classType,
        };

        try {
            setLoading(true);
            await createWordAndAddToStudySet(studySetId, payload);
            reload();
            handleClose(); // Close the modal
        } catch (error) {
            console.error("Error creating word:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateWord = async (id = "") => {
        const { title, translations, sentence, difficulty, meaning, classType } = newWordDetails;

        // Validation check for required fields
        if (!title || !translations || !studySetId) {
            alert(t("Please fill in all required fields."));
            return;
        }

        const payload = {
            _id: id,
            title,
            translations,
            sentence,
            difficulty,
            meaning,
            language: translations,  // assuming translation is for target language
            creationMethod: "manual",  // assuming manual creation method
            classType,
        };

        try {
            setLoading(true);
            await updateWordAndAddToStudySet(studySetId, payload);
            reload();
            handleClose(); // Close the modal
        } catch (error) {
            console.error("Error creating word:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} centered onHide={handleClose} size='lg'>
            <Modal.Header closeButton>
                <Modal.Title>{t("Add New Word")}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: "65vh", overflowY: "auto" }}>
                <Form>
                    <Form.Group className='mb-2' controlId="formTitle">
                        <Form.Label>{t("Study word")}<span style={{ color: 'red' }}> *</span></Form.Label>
                        <Form.Control
                            type="text"
                            value={newWordDetails.title}
                            onChange={(e) => setNewWordDetails({ ...newWordDetails, title: e.target.value })}
                            required
                        />
                    </Form.Group>

                    <Form.Group className='mb-2' controlId="formTranslations">
                        <Form.Label>{t("Translation")}<span style={{ color: 'red' }}> *</span></Form.Label>
                        <Form.Control
                            type="text"
                            value={newWordDetails.translations}
                            onChange={(e) => setNewWordDetails({ ...newWordDetails, translations: e.target.value })}
                            required
                        />
                    </Form.Group>

                    <Form.Group className='mb-2' controlId="formSentence">
                        <Form.Label>{t("Sentence")}<span className="text-muted ms-1">({t("optional")})</span></Form.Label>
                        <Form.Control
                            type="text"
                            value={newWordDetails.sentence}
                            onChange={(e) => setNewWordDetails({ ...newWordDetails, sentence: e.target.value })}
                        />
                    </Form.Group>

                    <Form.Group className='mb-2' controlId="formMeaning">
                        <Form.Label>{t("Meaning")}<span className="text-muted ms-1">({t("optional")})</span></Form.Label>
                        <Form.Control
                            type="text"
                            value={newWordDetails.meaning}
                            onChange={(e) => setNewWordDetails({ ...newWordDetails, meaning: e.target.value })}
                        />
                    </Form.Group>

                    <Form.Group className='mb-2' controlId="formDifficulty">
                        <Form.Label>{t("Difficulty")}<span className="text-muted ms-1">({t("optional")})</span></Form.Label>
                        <Form.Control
                            as="select"
                            value={newWordDetails.difficulty}
                            onChange={(e) => setNewWordDetails({ ...newWordDetails, difficulty: e.target.value })}
                        >
                            <option value="Beginner">{t("Beginner")}</option>
                            <option value="Elementary">{t("Elementary")}</option>
                            <option value="Intermediate">{t("Intermediate")}</option>
                            <option value="Advanced">{t("Advanced")}</option>
                            <option value="Expert">{t("Expert")}</option>
                        </Form.Control>
                    </Form.Group>

                    <Form.Group className='mb-2' controlId="formClassType">
                        <Form.Label>{t("Class Type")}<span className="text-muted ms-1">({t("optional")})</span></Form.Label>
                        <Form.Control
                            as="select"
                            value={newWordDetails.classType}
                            onChange={(e) => setNewWordDetails({ ...newWordDetails, classType: e.target.value })}
                        >
                            <option value="noun">{t("Noun")}</option>
                            <option value="verb">{t("Verb")}</option>
                            <option value="adjective">{t("Adjective")}</option>
                            <option value="adverb">{t("Adverb")}</option>
                            <option value="pronoun">{t("Pronoun")}</option>
                            <option value="preposition">{t("Preposition")}</option>
                            <option value="conjunction">{t("Conjunction")}</option>
                            <option value="determiner">{t("Determiner")}</option>
                            <option value="interjection">{t("Interjection")}</option>
                        </Form.Control>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer className='border-0 justify-content-end'>
                <Button disabled={loading} variant="primary" className='button-style' onClick={() => {
                    if (newWordDetails?._id) {
                        handleUpdateWord(newWordDetails?._id);
                    }
                    else {
                        handleCreateNewWord();
                    }
                }}>
                    {newWordDetails?._id ? t("Update") : t("Create")} {t("Word")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddNewWordModal;
